import { dayjs } from './dayjs-utils';
import { formatNumber } from './formattingUtils';
import { CreatorChallenge } from '../types/challenges';

export type ChallengeStatusVariant = 'success' | 'caution' | 'warning' | 'info' | 'critical';

export type CompletedChallengeStatus = 'claim' | { label: string; variant: ChallengeStatusVariant };

export const formatChallengeAmount = (amountCents: number, defaultCurrency: string) =>
  (amountCents / 100).toLocaleString('en-US', { style: 'currency', currency: defaultCurrency || 'USD' });

export const getChallengeTitle = (challenge: CreatorChallenge, defaultCurrency: string) => {
  const { reward, metric, triggerValue } = challenge;
  const prize =
    reward.type === 'free_product'
      ? 'Win a free product'
      : `Earn ${formatChallengeAmount(reward.amountCents, defaultCurrency)}`;
  const count = formatNumber(triggerValue);
  if (metric === 'posts') return `${prize} upon posting ${count} times`;
  return `${prize} upon crossing ${count} ${metric}`;
};

export const getChallengeProgressLabel = (challenge: CreatorChallenge) => {
  const { currentValue, targetValue } = challenge.progress;
  return `${formatNumber(currentValue)}/${formatNumber(targetValue)} ${challenge.metric}`;
};

export const isChallengeRewardClaimable = (challenge: CreatorChallenge) =>
  Boolean(challenge.reviewId) &&
  challenge.reviewStatus === 'approved' &&
  challenge.rewardStatus === 'awaiting_claim' &&
  challenge.reward.type === 'free_product';

export const isChallengeCompleted = (challenge: CreatorChallenge) =>
  challenge.progress.percentage >= 100 || challenge.rewardStatus === 'awaiting_claim';

export const formatChallengeEndDate = (endDate: string) => `Ends on ${dayjs(endDate).format('D MMM, YYYY')}`;

export const formatChallengeCompletedDate = (date: string | null) => (date ? dayjs(date).format('DD MMM, YYYY') : '-');

export const getChallengeRewardTypeLabel = (challenge: CreatorChallenge) =>
  challenge.reward.type === 'free_product' ? 'Free product' : 'Bonus';

export const getChallengeRewardProductNames = (challenge: CreatorChallenge) =>
  challenge.reward.type === 'free_product'
    ? challenge.reward.products.map(({ name }) => name).filter((name): name is string => Boolean(name))
    : [];

const toTitleCase = (value: string) =>
  value
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

const getDeliveryStatus = (deliveryStatus: string): CompletedChallengeStatus => {
  const status = deliveryStatus.toLowerCase();
  if (status === 'in_transit') return { label: 'In Transit', variant: 'info' };
  if (status === 'delivered') return { label: 'Delivered', variant: 'success' };
  return { label: toTitleCase(deliveryStatus), variant: 'warning' };
};

export const getCompletedChallengeStatus = (challenge: CreatorChallenge): CompletedChallengeStatus => {
  if (isChallengeRewardClaimable(challenge)) return 'claim';
  if (challenge.reviewStatus === 'rejected') return { label: 'Rejected', variant: 'critical' };
  if (challenge.rewardStatus === 'failed') return { label: 'Failed', variant: 'critical' };
  if (challenge.reward.type === 'flat_fee') {
    return challenge.rewardStatus === 'fulfilled'
      ? { label: 'Paid', variant: 'success' }
      : { label: 'Pending', variant: 'caution' };
  }
  if (challenge.deliveryStatus) return getDeliveryStatus(challenge.deliveryStatus);
  return { label: 'Pending', variant: 'caution' };
};
